import { NotifyPropertyChangedBase, Reflection } from "../../../../utilities/reflection";
import { transient, autoinject, computedFrom, observable, Container } from "aurelia-framework";
import { QueriesClientService } from "../../../../services/queries/queries-client.services";
import { PermissionEntity } from "../../../../models/manage/permissions.models";
import * as Enumerable from "linq";
import { UserModel } from "../../../../models/manage/user.models";
import { ManageUser } from "./users/manage-user";
import { ManageUsersClientService } from "../../../../services/manage/manage-users.service"; import { DialogServiceConstants } from "../../../../constants/basics/dialog.constants";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { I18N } from "aurelia-i18n";
import { UserClientService } from "../../../../services/authentication/user-client.service";
import { Permissions } from "./permissions";
import { Validations } from "./validations";
import { GenericConfirmationDialogModel } from "../../../../models/basics/generic-confirmation-dialog-model";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { BaseClaims } from "../../../../constants/authentication/base-claims";

@transient()
@autoinject
export class ManageUsers extends NotifyPropertyChangedBase {

    private _branchId: number = 0;
    private _promisesCount: number = 0;

    //permissions
    public canEditUser: boolean;
    public canDeleteUser: boolean;
    public canAddUser: boolean;
    public canAccessPermissions: boolean;
    public canAccessValidations: boolean;

    public users: any[] = [];

    //used for searching
    public isSearching: boolean = true;

    //used for compose
    public model: any = {};
    public vm: ManageUser | Validations | Permissions = null;

    //used for dropdown menu
    public userWithOpenMenu: UserModel = null;

    //used for paging
    public pageNumber: number = 0;
    public availableUsers: number;
    public pageCount: number = 25;
    public pageCountSet: any[] = [
        { label: 10, value: 10 },
        { label: 25, value: 25 },
        { label: 50, value: 50 },
        { label: 100, value: 100 },
        { label: 500, value: 500 }
    ];

    public selectedUser: UserModel;

    //used for deletions
    public userIdBeingDeleted: number;

    @observable()
    public query: string = "";

    constructor(private queriesClientService: QueriesClientService,
        private manageUsersService: ManageUsersClientService,
        private dialogClient: DialogClientService,
        private notificationService: NotificationClientService,
        private i18N: I18N,
        private userClientService: UserClientService,
        private permissions: Permissions,
        private validations: Validations,
        private container: Container,
        private userClaimsService: UserClaimsService) {
        super();
        this.model.back = () => { this.vm = null; }
        this.branchId = this.userClientService.currentUser.branchId;
        this.canAddUser = this.userClaimsService.hasPermission(BaseClaims.CanAddUsers);
        this.canEditUser = this.userClaimsService.hasPermission(BaseClaims.CanUpdateUsers);
        this.canDeleteUser = this.userClaimsService.hasPermission(BaseClaims.CanDeleteUsers);
        this.canAccessPermissions = this.userClaimsService.hasPermission(BaseClaims.CanAccessPermissions);
        this.canAccessValidations = this.userClaimsService.hasPermission(BaseClaims.CanAccessValidations);
    }

    set branchId(x: number) {
        const loadNewBranch = () => {
            this._branchId = x;
            this.selectedUser = null;
            this.pageNumber = 0;
            this.vm = null;
            if (x > 0) this.loadUsers();
        };

        if (this.vm) {
            this.canExitVM().then(() => loadNewBranch());
        }
        else {
            loadNewBranch();
        }
    }

    @computedFrom(Reflection.getPropertyPath<ManageUsers>(x => x._branchId))
    get branchId(): number {
        return this._branchId;
    }

    @computedFrom(Reflection.getPropertyPath<ManageUsers>(x => x.pageNumber))
    public get hasPrevious(): boolean {
        return this.pageNumber > 0;
    }

    @computedFrom(Reflection.getPropertyPath<ManageUsers>(x => x.pageNumber), Reflection.getPropertyPath<ManageUsers>(x => x.numberOfPages))
    public get hasNext(): boolean {
        return this.pageNumber < this.numberOfPages - 1;
    }

    @computedFrom(Reflection.getPropertyPath<ManageUsers>(x => x.availableUsers), Reflection.getPropertyPath<ManageUsers>(x => x.pageCount))
    public get numberOfPages(): number {
        const pages = Math.ceil(this.availableUsers / this.pageCount)
        //used so that when there are no search results 1/1 is displayed instead of 1/0
        return pages == 0 ? 1 : pages
    }

    queryChanged() {
        this.loadUsers();
        this.pageNumber = 0;
    }

    goToPermissions($event, user: UserModel) {
        if ($event) {
            $event.stopPropagation();
        }

        if (!this.canAccessPermissions || (user == this.selectedUser && this.vm instanceof Permissions)) {
            this.userWithOpenMenu = null;
            return;
        }

        const switchToPermissions = () => {
            this.manageUsersService.getRolesForUser(user.userId).then(x => {
                const roles = x.roles;

                this.vm = this.permissions;
                this.userWithOpenMenu = null;
                this.model = {
                    type: PermissionEntity.User,
                    id: user.userId,
                    roleIds: Enumerable.from(roles).where(x => x.roleId != null).select(x => x.roleId).toArray(),
                    departmentIds: Enumerable.from(roles).where(x => x.departmentId != null).select(x => x.departmentId).toArray(),
                    branchIds: [user.branchId],
                    employeeIds: [user.userId],
                    name: user.fullName,
                    cancelOperation: () => this.initializeVM(),
                    afterSaveOperation: () => {
                        const canAccessPermissions = this.userClaimsService.hasPermission(BaseClaims.CanAccessPermissions);
                        const canAccessValidations = this.userClaimsService.hasPermission(BaseClaims.CanAccessValidations);
                        const refreshMenuItems = this.canAccessPermissions != canAccessPermissions || this.canAccessValidations != canAccessValidations;
                        this.canAccessPermissions = canAccessPermissions;
                        this.canAccessValidations = canAccessValidations;

                        if (refreshMenuItems) {
                            this.vm = null;
                            this.selectedUser = null;
                            this.userWithOpenMenu = null;
                        }
                    }
                }
                this.selectedUser = user;
            });
        }

        if (this.vm) {
            this.canExitVM().then(() => switchToPermissions());
        }
        else {
            switchToPermissions();
        }
    }

    goToValidations($event, user: UserModel) {
        if ($event) {
            $event.stopPropagation();
        }

        if (!this.canAccessValidations || user == this.selectedUser && this.vm instanceof Validations) {
            this.userWithOpenMenu = null;
            return;
        }

        const switchToValidations = () => {
            this.vm = this.validations;
            this.userWithOpenMenu = null;
            this.model = {
                type: PermissionEntity.User,
                id: user.userId,
                name: user.fullName,
                calledFromManageUsers: true,
                usePassedCancelOperation: true,
                cancelOperation: () => this.initializeVM()
            }
            this.selectedUser = user;
        }

        if (this.vm) {
            this.canExitVM().then(() => switchToValidations());
        }
        else {
            switchToValidations();
        }
    }

    editUser(user: UserModel) {
        if (!this.canEditUser || (user == this.selectedUser && this.vm instanceof ManageUser)) {
            this.userWithOpenMenu = null;
            return;
        }

        const editOperation = () => {
            this.vm = null;
            this.vm = this.container.get(ManageUser) as ManageUser;
            this.selectedUser = user;
            this.userWithOpenMenu = null;
            this.model = {
                userId: user.userId,
                reload: () => this.reloadList(),
                close: () => this.initializeVM()
            };
        };

        if (this.vm) {
            this.canExitVM().then(() => editOperation());
        }
        else {
            editOperation();
        }
    }

    public newUser(): void {
        if (!this.canAddUser) return;

        const addOperation = () => {
            this.vm = null;
            this.vm = this.container.get(ManageUser) as ManageUser;
            this.selectedUser = null;
            this.model = {
                userId: 0,
                reload: () => this.reloadList(),
                close: () => this.initializeVM(),
                branchId: this.branchId
            }
        }

        if (this.vm) {
            this.canExitVM().then(() => addOperation());
        }
        else {
            addOperation();
        }
    }

    loadUsers() {
        let filterPart = `$filter=branchId eq ${this.branchId}`;
        if (this.query) {
            filterPart += ` and (contains(fullName, '${this.query}') or contains(email, '${this.query}') or contains(departmentAndRole, '${this.query}'))`
        }

        const dataPromise = this.queriesClientService.getData(`odata/Users?${filterPart}&$orderby=fullName&$top=${this.pageCount}&$skip=${this.pageCount * this.pageNumber}&$count=true`);
        this._promisesCount++;
        const localPromiseCount = this._promisesCount;

        dataPromise.then(response => {
            if (localPromiseCount != this._promisesCount) return;

            this.users = response.value;
            this.users.forEach(x => {
                if (x.departmentAndRole) x.departmentAndRole = x.departmentAndRole.replace(/,/g, ', ');
            });
            this.availableUsers = response["@odata.count"];
        });
    }

    pageCountChanged() {
        this.reloadList();
        this.loadUsers();
    }

    public showDelete($event, user: UserModel): void {
        if (!this.canDeleteUser) return;

        if ($event) {
            $event.stopPropagation();
        }

        this.dialogClient.show(DialogServiceConstants.URLs.DeleteModal, this);
        this.userIdBeingDeleted = user.userId;
    }

    public delete(): void | Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.manageUsersService.delete({
                userIds: [this.userIdBeingDeleted]
            })
                .then(() => {
                    this.dialogClient.hide();
                    this.notificationService.show(this.i18N.tr('successfully-deleted-text'), this.i18N.tr('user-has-been-successfully-deleted-text'), NotificationType.Success);
                    this.reloadList();
                    resolve();
                },  () => {
                    this.notificationService.show(this.i18N.tr('warning') + "!", this.i18N.tr('user-cannot-be-deleted-in-use-text'), NotificationType.Warning);
                    reject();
                });
        });
    }

    public reloadList(): void {
        const reloadOperation = () => {
            this.selectedUser = null;
            this.pageNumber = 0;
            this.loadUsers();
            this.vm = null;
        }

        if (this.vm) {
            this.canExitVM().then(() => reloadOperation());
        }
        else {
            reloadOperation();
        }
    }

    public initializeVM(): void {
        this.canExitVM().then(() => {
            this.vm = null;
            this.selectedUser = null;
            this.userWithOpenMenu = null;
        })
    }

    changeSearchFieldVisibility() {
        this.isSearching = !this.isSearching;
        this.query = "";
    }

    previousClick() {
        this.pageNumber -= 1;
        this.loadUsers();
    }

    nextClick() {
        this.pageNumber += 1;
        this.loadUsers();
    }

    canExitVM() {
        return new Promise<any>((resolve, reject) => {
            if (this.selectedUser && this.selectedUser.userId == this.userIdBeingDeleted) {
                this.userIdBeingDeleted = -1;
                resolve(true);
            }
            else if (this.vm.areThereUnsavedChanges()) {
                const model = new GenericConfirmationDialogModel();
                model.confirm = this.i18N.tr("yes-text");
                model.cancel = this.i18N.tr("cancel-text");
                model.messages.push(this.i18N.tr("unsaved-changes-text"));
                model.title = this.i18N.tr("discard-unsaved-changes-text");
                model.onConfirm = () => resolve(true);
                model.onCancel = () => reject(false);

                this.dialogClient.show(DialogServiceConstants.URLs.GenericConfirmationDialog, model, false, false);
            }
            else {
                resolve(true);
            }
        });
    }

    public openUserMenu($event, user) {
        $event.stopPropagation();

        if (this.userWithOpenMenu == user) {
            this.userWithOpenMenu = null;
        }
        else {
            this.userWithOpenMenu = user;
        }
    }

    public onUserMenuBlur() {
        this.userWithOpenMenu = null;
    }
}
